import React from 'react'
import { Box, Button } from '@mui/material'

import { AppBrandMark, AppEmptyLine } from '@components'
import { dsColorNew } from '@palette'
import Cell from './Cell'
import { filterRows, sortRows } from '../@services/model'
import { fixedRowSubs, noCompetitorsText } from '../../../@services/market'
import { money, ordinal, signedMoney, signedPct } from '../@services/format'


const MEDIAN = '__median'

const sx = {
	wrap: { overflow: 'auto', maxHeight: 'calc(100vh - 210px)', border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '10px', position: 'relative' },
	table: {
		borderCollapse: 'separate', borderSpacing: 0, fontSize: 13, minWidth: '100%',
		'& th': {
			position: 'sticky', top: 0, zIndex: 3, backgroundColor: dsColorNew.tableHead, color: 'rgba(255,255,255,.92)',
			fontWeight: 700, fontSize: 13, px: 0.75, py: 1, textAlign: 'center', whiteSpace: 'nowrap', border: 0, lineHeight: 1.25,
		},
		'& th small': { display: 'block', fontWeight: 500, fontSize: 11, color: 'rgba(255,255,255,.62)' },
		'& th.rowh': { left: 0, zIndex: 5, textAlign: 'left', pl: 2, minWidth: 168 },
		'& th.today': { boxShadow: `inset 0 -3px 0 ${dsColorNew.cyan}` },
		'& td': { borderBottom: `1px solid ${dsColorNew.grayBorder}`, p: '4px 3px', backgroundColor: dsColorNew.white, textAlign: 'center', verticalAlign: 'middle' },
		'& td.rowh': { position: 'sticky', left: 0, zIndex: 2, textAlign: 'left', p: '6px 12px 6px 16px', whiteSpace: 'nowrap', borderRight: `1px solid ${dsColorNew.grayBorder}`, minWidth: 168 },
		'& tr.comp:nth-of-type(even) td': { backgroundColor: dsColorNew.tableRowAlt },
		'& tr.comp:hover td': { backgroundColor: dsColorNew.hoverBgLight },
		'& tr.you td': { backgroundColor: '#F3F8FE' },
		'& tr.you td.rowh': { boxShadow: `inset 3px 0 0 ${dsColorNew.appBlue}` },
		'& tr.median td': { backgroundColor: '#FAFAFB' },
		'& tr.stand td': { backgroundColor: '#FCFCFD', fontSize: 12.5, py: 0.75 },
		'& tr.stand td.rowh': { color: dsColorNew.textMuted, fontWeight: 500 },
		'& tr.block-end td': { borderBottom: '2px solid #CBD2DE' },
		'& tr.sep td': {
			backgroundColor: dsColorNew.white, p: '10px 16px 6px', textAlign: 'left', fontSize: 11, fontWeight: 600, letterSpacing: 0.6,
			textTransform: 'uppercase', color: dsColorNew.textSubtle,
		},
	},
	prov: { display: 'inline-flex', alignItems: 'center', gap: 1, fontWeight: 600, color: dsColorNew.textTitle },
	sub: { display: 'block', fontSize: 11.5, color: dsColorNew.textMuted, fontWeight: 400, mt: '1px' },
	pill: { display: 'inline-flex', alignItems: 'center', px: 1, borderRadius: '999px', fontSize: 12, fontWeight: 600, lineHeight: 1.5 },
}

const youPill = <Box component="span" sx={{ ...sx.pill, backgroundColor: dsColorNew.pillActiveBg, color: dsColorNew.appBlue }}>You</Box>
const yoursPill = <Box component="span" sx={{ ...sx.pill, backgroundColor: dsColorNew.pillTrackBg, color: dsColorNew.textDark }}>Yours</Box>

function Provider({ provider, pill }) {
	return <Box component="span" sx={sx.prov}><AppBrandMark name={provider.name} size={18}/>{provider.name}{pill && <> {pill}</>}</Box>
}

function Position({ stand, code }) {
	if (!stand.pos) return <Box component="span" sx={{ color: dsColorNew.textSubtle }}>—</Box>
	const first = stand.pos === 1
	return <>
		<Box component="span" sx={{
			display: 'inline-flex', px: 1.1, py: '2px', borderRadius: '999px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
			backgroundColor: first ? dsColorNew.successBg : dsColorNew.pillTrackBg, color: first ? dsColorNew.successText : dsColorNew.textDark,
		}}>{ordinal(stand.pos)} of {stand.total}</Box>
		{first
			? <Box component="span" sx={{ display: 'block', mt: '3px', fontSize: 11, fontWeight: 500, color: dsColorNew.textMuted, whiteSpace: 'nowrap' }}>
				{stand.gap.who ? `cheapest · ${money(stand.gap.e, code)} below ${stand.gap.who}` : 'cheapest'}
			</Box>
			: <Box component="span" sx={{ display: 'block', mt: '3px', fontSize: 11, fontWeight: 600, color: dsColorNew.dangerRed, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
				▲ {signedMoney(stand.gap.e, code)} · {signedPct(stand.gap.p)}
				<Box component="small" sx={{ display: 'block', fontWeight: 500, color: dsColorNew.textMuted, fontSize: 11 }}>vs {stand.gap.who}</Box>
			</Box>}
	</>
}

function Cheapest({ stand, code }) {
	if (!stand.cheapest) return '—'
	const p = stand.cheapest.row.provider
	return <Box component="span" sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '1px', fontSize: 12, lineHeight: 1.25 }}>
		<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, fontWeight: 600, color: dsColorNew.textTitle, whiteSpace: 'nowrap' }}>
			<AppBrandMark name={p.name} size={16}/>{p.name}{p.own === 'primary' && youPill}{p.own === 'other' && yoursPill}
		</Box>
		<Box component="span" sx={{ color: dsColorNew.successText, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{money(stand.cheapest.v, code)}</Box>
	</Box>
}

/**
 * Provider × pick-up date/week (CA 11-14, 16-17): your brand, the market median, your position and the cheapest stay
 * on top whatever the order; your other brands, then the competitors, sorted and searched locally. The provider column
 * and the header stay fixed while scrolling. With Tiers / Providers (`model.filtered`, E3) the rows on top say they
 * are computed among the providers shown, and the separator says how many of them.
 */
export default function Grid({ model, headers, view, code, search, sort, onOpen, onClearSearch, tierName = null, onClearMarket }) {
	const { columns, you, others, competitors, median, stand, identity, filtered, counts } = model
	const subs = fixedRowSubs({ filtered, identity, competitors: counts?.competitors ?? competitors.length })
	const span = columns.length + 1
	const cheapestId = j => stand[j].cheapest?.row.provider.id

	const cells = (row, rowKey, name) => row.cells.map((c, j) => <td key={columns[j].key}>
		<Cell cell={c} rowName={name} columnLabel={headers[j].label} cmp={view.cmp} code={code} showPrev={view.prev}
			cheapest={rowKey !== MEDIAN && cheapestId(j) === rowKey} onOpen={() => onOpen(rowKey, j)}/>
	</td>)

	const mine = filterRows(others, search)
	const sorted = sortRows(competitors, sort)
	const shown = filterRows(sorted, search)
	const youHasPrices = !!you && you.cells.some(c => c.cur)

	return <Box sx={sx.wrap}>
		<Box component="table" sx={sx.table}>
			<thead><tr>
				<th className="rowh">Provider</th>
				{headers.map((h, j) => <th key={columns[j].key} className={h.isToday ? 'today' : ''} title={h.title}>{h.label}<small>{h.sub}</small></th>)}
			</tr></thead>
			<tbody>
				{!!identity && !!you && <tr className="you">
					<td className="rowh"><Provider provider={you.provider} pill={youPill}/></td>
					{youHasPrices
						? cells(you, you.provider.id, you.provider.name)
						: <td colSpan={columns.length} style={{ textAlign: 'left', paddingLeft: 16, fontSize: 12.5, color: dsColorNew.textMuted }}>No prices of your brand for this office and dates.</td>}
				</tr>}
				<tr className={`median${identity ? '' : ' block-end'}`}>
					<td className="rowh">
						<Box component="span" sx={sx.prov}><AppBrandMark name="Market median" size={18}/>Market median</Box>
						<Box component="span" sx={sx.sub}>{subs ? subs.median : identity ? 'Your competitors, your brands excluded' : 'All the providers below'}</Box>
					</td>
					{median.map((c, j) => <td key={columns[j].key}>
						<Cell cell={c} rowName="Market median" columnLabel={headers[j].label} cmp={view.cmp} code={code} showPrev={view.prev} onOpen={() => onOpen(MEDIAN, j)}/>
					</td>)}
				</tr>
				{!!identity && <tr className="stand">
					<td className="rowh">Your position <Box component="span" sx={sx.sub}>{subs ? subs.position : '1 = the cheapest · gap to the cheapest'}</Box></td>
					{stand.map((s, j) => <td key={columns[j].key}>{youHasPrices ? <Position stand={s} code={code}/> : <Box component="span" sx={{ color: dsColorNew.textSubtle }}>—</Box>}</td>)}
				</tr>}
				<tr className="stand block-end">
					<td className="rowh">Cheapest{!!subs && <Box component="span" sx={sx.sub}>{subs.cheapest}</Box>}</td>
					{stand.map((s, j) => <td key={columns[j].key}><Cheapest stand={s} code={code}/></td>)}
				</tr>
				{!!mine.length && <>
					<tr className="sep"><td colSpan={span}>Your other brands · {mine.length} <Box component="span" sx={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>— not counted in the market median or in your position</Box></td></tr>
					{mine.map(r => <tr key={r.provider.id} className="comp">
						<td className="rowh"><Provider provider={r.provider} pill={yoursPill}/></td>
						{cells(r, r.provider.id, r.provider.name)}
					</tr>)}
				</>}
				<tr className="sep"><td colSpan={span}>Competitors · {search?.trim() ? `${shown.length} of ${sorted.length}` : filtered ? `${sorted.length} of ${counts.allCompetitors}` : sorted.length}</td></tr>
				{filtered && !competitors.length && <tr><td colSpan={span} style={{ textAlign: 'left', paddingLeft: 16 }}>
					<AppEmptyLine>{noCompetitorsText(tierName)} <Button variant="text" size="small" onClick={onClearMarket} sx={{ textTransform: 'none', minWidth: 0, p: '2px 4px' }}>Clear filters</Button></AppEmptyLine>
				</td></tr>}
				{!shown.length && !mine.length && !!search?.trim() && <tr><td colSpan={span} style={{ textAlign: 'left', paddingLeft: 16 }}>
					<AppEmptyLine>No provider matches “{search}”. <Button variant="text" size="small" onClick={onClearSearch} sx={{ textTransform: 'none', minWidth: 0, p: '2px 4px' }}>Clear search</Button></AppEmptyLine>
				</td></tr>}
				{shown.map(r => <tr key={r.provider.id} className="comp">
					<td className="rowh"><Provider provider={r.provider}/></td>
					{cells(r, r.provider.id, r.provider.name)}
				</tr>)}
			</tbody>
		</Box>
	</Box>
}

export { MEDIAN }
